import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { KeyRound, PlugZap, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Field, Input, Select, Spinner } from '../../components/ui';
import { errorMessage, get, post, put } from '../../lib/api';
import { dateTimeOf } from '../../lib/format';

const tokens = (n: number) => n.toLocaleString('pt-BR');
const localDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'America/Cuiaba' });

type Provider = 'none' | 'gemini' | 'anthropic';

interface AiModel {
  id: string;
  provider: 'gemini' | 'anthropic';
  label: string;
  inputPerMillion: number;
  outputPerMillion: number;
  note: string | null;
  recommended?: boolean;
  estimatedCostPerSummary: number;
}

interface AiConfigView {
  provider: Provider;
  model: string;
  defaultModels: Record<'gemini' | 'anthropic', string>;
  keys: Record<'gemini' | 'anthropic', { configured: boolean; hint: string | null; updatedAt: string | null }>;
  models: AiModel[];
  features: AiFeatureView[];
  /** Limite diário (US$) dos recursos automáticos; null = sem limite. */
  dailyLimitUsd: number | null;
  automaticSpentToday: number;
  automaticPaused: boolean;
}

interface AiFeatureView {
  id: string;
  label: string;
  description: string;
  automatic: boolean;
  /** Recebe áudio: só modelos do Gemini. */
  audio: boolean;
  enabled: boolean;
  /** Modelo escolhido para o recurso; null = o padrão. */
  model: string | null;
  /** Modelo que o recurso usa de fato (o escolhido ou o padrão). */
  effectiveModel: string | null;
  /** Custo estimado de um uso com esse modelo (US$); null = sem preço ou o modelo não serve (áudio). */
  estimatedCostPerUse: number | null;
  /** Volume por mês das premissas (docs/ia-custos.md). */
  assumedPerMonth: number;
  /** Uso real nos últimos 30 dias. */
  last30Days: { calls: number; costUsd: number };
}

interface UsageRow {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

interface UsageView {
  total: UsageRow;
  byFeature: Array<UsageRow & { feature: string; label: string; model: string; modelLabel: string }>;
  byDay: Array<UsageRow & { day: string }>;
}

const PROVIDERS: Array<{ value: Provider; label: string }> = [
  { value: 'none', label: 'Desligada' },
  { value: 'gemini', label: 'Gemini (Google)' },
  { value: 'anthropic', label: 'Claude (Anthropic)' },
];

const KEY_HELP: Record<'gemini' | 'anthropic', string> = {
  gemini: 'Crie em aistudio.google.com (Get API key), num projeto do Google Cloud COM faturamento ativado: no nível gratuito o Google usa o conteúdo enviado para melhorar os produtos.',
  anthropic: 'Crie em platform.claude.com (API keys). Pela política da Anthropic, dados enviados pela API não são usados para treinar modelos.',
};

const PROVIDER_NAME: Record<'gemini' | 'anthropic', string> = { gemini: 'Gemini', anthropic: 'Claude' };

const usd = (v: number) => `US$ ${v < 0.01 ? v.toFixed(4) : v.toFixed(2)}`;

/**
 * Administração → IA: provedor, modelo e chave de API (cifrada no banco; a tela só vê os 4 últimos
 * caracteres). Vale na hora, sem reiniciar. Hoje usada no "Gerar resumo com IA" do Finalizar.
 */
export function AiAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['ai-config'], queryFn: () => get<AiConfigView>('/ai/config') });
  const [provider, setProvider] = useState<Provider>('none');
  const [model, setModel] = useState('');
  const [newKey, setNewKey] = useState('');
  const [perMonth, setPerMonth] = useState(2200);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  useEffect(() => {
    if (!data) return;
    setProvider(data.provider);
    setModel(data.model);
  }, [data]);

  if (isLoading) return <Spinner />;
  if (!data) return <ErrorText error={error} />;

  const p = provider === 'none' ? null : provider;
  const models = p ? data.models.filter((m) => m.provider === p) : [];
  const effectiveModel = p ? model || data.defaultModels[p] : '';
  const key = p ? data.keys[p] : null;
  const dirty = provider !== data.provider || model !== data.model || newKey.trim() !== '';
  const customModel = Boolean(model) && !models.some((m) => m.id === model);

  const save = async (apiKeys?: Record<string, string | null>) => {
    setSaving(true);
    setSaveError(null);
    try {
      await put('/ai/config', { provider, model: p ? model : '', ...(apiKeys ? { apiKeys } : p && newKey.trim() ? { apiKeys: { [p]: newKey.trim() } } : {}) });
      setNewKey('');
      toast('Configuração da IA salva', 'ok');
      void qc.invalidateQueries({ queryKey: ['ai-config'] });
      void qc.invalidateQueries({ queryKey: ['settings', 'attendance'] });
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await post<{ model: string; reply: string; ms: number }>('/ai/test');
      toast(`IA respondeu (${r.model}, ${(r.ms / 1000).toFixed(1)} s): ${r.reply}`, 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <p className="text-sm text-fg-soft">
        A IA ajuda no atendimento (resumo no Finalizar, sugestão do chamado, transcrição de áudios, histórico do cliente) e na gestão (urgência e humor na
        fila, alerta de cliente irritado, assuntos do período). Cada recurso liga e desliga abaixo. As conversas são enviadas ao provedor escolhido. Cada
        alteração fica na auditoria (sem a chave).
      </p>

      <div className="grid gap-4 rounded-lg border border-border bg-panel p-4 md:grid-cols-2">
        <Field label="Provedor">
          <Select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value as Provider);
              setModel('');
              setNewKey('');
            }}
          >
            {PROVIDERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        {p && (
          <Field label="Modelo" hint={customModel ? '(fora da lista)' : undefined}>
            <Select value={customModel ? '__custom' : model} onChange={(e) => setModel(e.target.value === '__custom' ? data.defaultModels[p] : e.target.value)}>
              <option value="">Padrão ({models.find((m) => m.id === data.defaultModels[p])?.label ?? data.defaultModels[p]})</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {m.recommended ? ' (recomendado)' : ''}
                </option>
              ))}
              {customModel && <option value="__custom">{model}</option>}
            </Select>
          </Field>
        )}

        {p && key && (
          <div className="md:col-span-2">
            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  <KeyRound className="size-3.5" /> Chave de API
                  {key.configured ? (
                    <Badge tone="ok">gravada, termina em {key.hint}</Badge>
                  ) : (
                    <Badge tone="warn">não configurada</Badge>
                  )}
                </span>
              }
              hint={key.updatedAt ? `(alterada em ${dateTimeOf(key.updatedAt)})` : undefined}
            >
              <div className="flex gap-2">
                <Input
                  type="password"
                  autoComplete="off"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder={key.configured ? 'Cole uma chave nova só para trocar' : 'Cole a chave de API'}
                />
                {key.configured && (
                  <Button
                    variant="ghost"
                    disabled={saving}
                    onClick={() => {
                      if (window.confirm('Apagar a chave gravada? A IA fica desligada até informar outra.')) {
                        setProvider('none');
                        void save({ [p]: null });
                      }
                    }}
                  >
                    Apagar chave
                  </Button>
                )}
              </div>
            </Field>
            <p className="mt-1 text-xs text-fg-soft">{KEY_HELP[p]}</p>
          </div>
        )}

        <div className="flex items-center gap-2 md:col-span-2">
          <Button variant="primary" disabled={!dirty} loading={saving} onClick={() => void save()}>
            Salvar
          </Button>
          {data.provider !== 'none' && !dirty && (
            <Button onClick={() => void test()} loading={testing} title="Pedido mínimo ao provedor para conferir a chave e o modelo">
              <PlugZap className="size-4" /> Testar
            </Button>
          )}
          {data.provider !== 'none' && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-ok">
              <Sparkles className="size-3.5" /> Ligada: {data.models.find((m) => m.id === (data.model || data.defaultModels[data.provider as 'gemini']))?.label ?? data.model}
            </span>
          )}
        </div>
        <div className="md:col-span-2">
          <ErrorText error={saveError} />
        </div>
      </div>

      {data.provider !== 'none' && <OtherKey config={data} provider={data.provider === 'gemini' ? 'anthropic' : 'gemini'} />}

      {data.provider !== 'none' && <AiFeatures config={data} />}
      {data.provider !== 'none' && <CostForecast config={data} />}
      {data.provider !== 'none' && <AiUsage />}

      {p && (
        <section>
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xs font-semibold tracking-wide text-fg-soft uppercase">Comparar modelos ({PROVIDER_NAME[p]}): custo de um resumo</h2>
            <label className="flex items-center gap-2 text-xs text-fg-soft">
              Resumos por mês
              <Input type="number" min={0} value={perMonth} onChange={(e) => setPerMonth(Math.max(0, Number(e.target.value) || 0))} className="w-24 text-xs" />
            </label>
          </div>
          <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
            <thead className="bg-muted text-left text-xs text-fg-soft">
              <tr>
                <th className="px-3 py-2">Modelo</th>
                <th className="px-3 py-2 text-right" title="US$ por milhão de tokens">Entrada</th>
                <th className="px-3 py-2 text-right" title="US$ por milhão de tokens">Saída</th>
                <th className="px-3 py-2 text-right">Por resumo</th>
                <th className="px-3 py-2 text-right">Por mês</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} className={clsx('border-t border-border', m.id === effectiveModel && 'bg-accent/5')}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {m.label}
                      {m.recommended && <Badge tone="ok">recomendado</Badge>}
                      {m.id === effectiveModel && <Badge tone="accent">escolhido</Badge>}
                    </div>
                    {m.note && <div className="text-xs text-fg-soft">{m.note}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{usd(m.inputPerMillion)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{usd(m.outputPerMillion)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{usd(m.estimatedCostPerSummary)}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{usd(m.estimatedCostPerSummary * perMonth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-[11px] text-fg-soft">
            Para escolher o modelo padrão: estimativa com ~4 mil tokens de conversa e ~250 de resumo por pedido (mais o raciocínio). A previsão com o modelo de
            cada recurso está em "Custo previsto por recurso". Preços de outubro de 2026, em dólar, sem impostos; confira o consumo real no painel do provedor.
            Detalhes em docs/ia-custos.md.
          </p>
        </section>
      )}
    </div>
  );
}

/**
 * Chave do provedor que não é o padrão: serve para escolher modelos dele em recursos específicos
 * (ex.: transcrição no Gemini e as análises no Claude Haiku 5.5).
 */
function OtherKey({ config, provider }: { config: AiConfigView; provider: 'gemini' | 'anthropic' }) {
  const qc = useQueryClient();
  const key = config.keys[provider];
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState<'save' | 'delete' | 'test' | null>(null);
  const send = async (apiKey: string | null) => {
    setBusy(apiKey === null ? 'delete' : 'save');
    try {
      await put('/ai/config', { provider: config.provider, model: config.model, apiKeys: { [provider]: apiKey } });
      setValue('');
      toast(apiKey === null ? 'Chave apagada' : 'Chave salva', 'ok');
      void qc.invalidateQueries({ queryKey: ['ai-config'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };
  const test = async () => {
    setBusy('test');
    try {
      const r = await post<{ model: string; reply: string; ms: number }>('/ai/test', { model: config.defaultModels[provider] });
      toast(`${PROVIDER_NAME[provider]} respondeu (${r.model}, ${(r.ms / 1000).toFixed(1)} s): ${r.reply}`, 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="rounded-lg border border-border bg-panel p-4">
      <Field
        label={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <KeyRound className="size-3.5" /> Chave do {PROVIDER_NAME[provider]} (para recursos com outro modelo)
            {key.configured ? <Badge tone="ok">gravada, termina em {key.hint}</Badge> : <Badge>não configurada</Badge>}
          </span>
        }
        hint={key.updatedAt ? `(alterada em ${dateTimeOf(key.updatedAt)})` : undefined}
      >
        <div className="flex flex-wrap gap-2">
          <Input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={key.configured ? 'Cole uma chave nova só para trocar' : 'Cole a chave de API'}
            className="min-w-0 flex-1"
          />
          <Button disabled={!value.trim()} loading={busy === 'save'} onClick={() => void send(value.trim())}>
            Salvar chave
          </Button>
          {key.configured && (
            <>
              <Button onClick={() => void test()} loading={busy === 'test'} title="Pedido mínimo ao provedor para conferir a chave">
                <PlugZap className="size-4" /> Testar
              </Button>
              <Button variant="ghost" loading={busy === 'delete'} onClick={() => window.confirm('Apagar esta chave?') && void send(null)}>
                Apagar chave
              </Button>
            </>
          )}
        </div>
      </Field>
      <p className="mt-1 text-xs text-fg-soft">{KEY_HELP[provider]}</p>
    </section>
  );
}

/** Recursos de IA: cada um liga e desliga na hora (os automáticos gastam tokens a cada mensagem) e pode usar outro modelo. */
function AiFeatures({ config }: { config: AiConfigView }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const defaultId = config.provider === 'none' ? '' : config.model || config.defaultModels[config.provider];
  const defaultLabel = config.models.find((m) => m.id === defaultId)?.label ?? defaultId;
  const change = async (id: string, body: Record<string, unknown>) => {
    setBusy(id);
    try {
      await put('/ai/config', { provider: config.provider, model: config.model, ...body });
      void qc.invalidateQueries({ queryKey: ['ai-config'] });
      void qc.invalidateQueries({ queryKey: ['settings', 'attendance'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };
  const toggle = (id: string, enabled: boolean) => change(id, { features: { [id]: enabled } });
  const choose = (id: string, model: string) => change(id, { featureModels: { [id]: model || null } });
  /** Modelos que o recurso pode usar: provedores com chave (e o já escolhido); áudio só no Gemini. */
  const optionsFor = (f: AiFeatureView) =>
    (['gemini', 'anthropic'] as const)
      .filter((prov) => !f.audio || prov === 'gemini')
      .map((prov) => ({ prov, models: config.models.filter((m) => m.provider === prov && (config.keys[prov].configured || m.id === f.model)) }))
      .filter((g) => g.models.length);
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Recursos</h2>
      <div className="divide-y divide-border rounded-lg border border-border bg-panel">
        {config.features.map((f) => {
          const audioOnDefault = f.audio && !f.model && config.provider !== 'gemini';
          return (
            <div key={f.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-3 py-2.5">
              <label className="flex min-w-0 flex-1 basis-64 cursor-pointer items-start gap-3">
                <input type="checkbox" className="mt-1" checked={f.enabled} disabled={busy === f.id} onChange={(e) => void toggle(f.id, e.target.checked)} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    {f.label}
                    {f.automatic && <Badge tone="warn">automático</Badge>}
                  </span>
                  <span className="block text-xs text-fg-soft">{f.description}</span>
                  {audioOnDefault && <span className="block text-xs text-warn">O padrão não recebe áudio: escolha um modelo do Gemini para transcrever.</span>}
                </span>
              </label>
              <Select
                value={f.model ?? ''}
                disabled={busy === f.id}
                onChange={(e) => void choose(f.id, e.target.value)}
                className="w-full text-xs md:w-56"
                title="Modelo usado por este recurso"
              >
                <option value="">Padrão ({defaultLabel})</option>
                {optionsFor(f).map((g) => (
                  <optgroup key={g.prov} label={PROVIDER_NAME[g.prov]}>
                    {g.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-[11px] text-fg-soft">
        Automático = roda sozinho, sem ninguém pedir; o custo acompanha o volume de mensagens. A análise da fila fica parada enquanto a equipe está fora do
        horário de atendimento (Administração → Horários) e analisa cada cliente uma vez quando abre. Cada recurso pode usar outro modelo, de qualquer
        provedor com chave gravada (ex.: análises no Claude Haiku 5.5, o mais barato; a transcrição só no Gemini, que recebe áudio).
      </p>
      <DailyLimit config={config} />
    </section>
  );
}

/** Limite de gasto por dia dos recursos automáticos (o que alguém pede não para). */
function DailyLimit({ config }: { config: AiConfigView }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(config.dailyLimitUsd === null ? '' : String(config.dailyLimitUsd));
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(config.dailyLimitUsd === null ? '' : String(config.dailyLimitUsd)), [config.dailyLimitUsd]);
  const parsed = value.trim() === '' ? null : Number(value.replace(',', '.'));
  const dirty = parsed !== config.dailyLimitUsd;
  const save = async () => {
    setSaving(true);
    try {
      await put('/ai/config', { provider: config.provider, model: config.model, dailyLimitUsd: parsed });
      toast('Limite diário salvo', 'ok');
      void qc.invalidateQueries({ queryKey: ['ai-config'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={clsx('mt-3 rounded-lg border bg-panel p-3', config.automaticPaused ? 'border-warn' : 'border-border')}>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Limite diário dos recursos automáticos (US$)" hint="(vazio = sem limite)">
          <Input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" placeholder="sem limite" className="w-32" />
        </Field>
        <Button disabled={!dirty || (parsed !== null && !(parsed > 0))} loading={saving} onClick={() => void save()} className="mb-3">
          Salvar limite
        </Button>
        <div className="mb-3 text-sm">
          Gasto automático hoje: <b className="tabular-nums">{usd(config.automaticSpentToday)}</b>
          {config.dailyLimitUsd !== null && <span className="text-fg-soft"> de {usd(config.dailyLimitUsd)}</span>}
        </div>
      </div>
      {config.automaticPaused ? (
        <p className="text-xs font-medium text-warn">
          Limite atingido: urgência e humor, alerta de cliente irritado e transcrição automática estão parados até amanhã. O que os operadores pedem (resumo,
          sugestão, histórico, transcrição no Finalizar) continua funcionando.
        </p>
      ) : (
        <p className="text-[11px] text-fg-soft">
          Passou do limite, os recursos automáticos param até o dia seguinte (fuso da empresa); os áudios esperam e são transcritos depois. Os botões que os
          operadores usam não param.
        </p>
      )}
    </div>
  );
}

/**
 * Custo previsto por mês de cada recurso com o modelo que ele usa de fato (o escolhido no recurso ou o
 * padrão). Volume: o real dos últimos 30 dias, quando houve uso; senão a premissa; editável na hora.
 */
function CostForecast({ config }: { config: AiConfigView }) {
  const [volume, setVolume] = useState<Record<string, number>>({});
  const label = (id: string | null) => (id ? (config.models.find((m) => m.id === id)?.label ?? id) : '—');
  const rows = config.features.map((f) => {
    const real = f.last30Days.calls > 0;
    const perMonth = volume[f.id] ?? (real ? f.last30Days.calls : f.assumedPerMonth);
    return { f, real, perMonth, monthly: f.estimatedCostPerUse === null ? null : f.estimatedCostPerUse * perMonth };
  });
  const total = rows.reduce((sum, r) => sum + (r.f.enabled && r.monthly !== null ? r.monthly : 0), 0);
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Custo previsto por recurso (por mês)</h2>
      <div className="overflow-x-auto">
        <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Recurso</th>
              <th className="px-3 py-2">Modelo em uso</th>
              <th className="px-3 py-2 text-right">Por uso</th>
              <th className="px-3 py-2 text-right">Usos por mês</th>
              <th className="px-3 py-2 text-right">Por mês</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ f, real, perMonth, monthly }) => (
              <tr key={f.id} className={clsx('border-t border-border', !f.enabled && 'text-fg-soft')}>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {f.label}
                    {!f.enabled && <Badge>desligado</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {label(f.effectiveModel)}
                  {!f.model && f.effectiveModel && <span className="text-xs text-fg-soft"> (padrão)</span>}
                  {f.audio && f.estimatedCostPerUse === null && <div className="text-xs text-warn">não recebe áudio</div>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{f.estimatedCostPerUse === null ? '—' : usd(f.estimatedCostPerUse)}</td>
                <td className="px-3 py-2 text-right">
                  <Input
                    type="number"
                    min={0}
                    value={perMonth}
                    onChange={(e) => setVolume((v) => ({ ...v, [f.id]: Math.max(0, Number(e.target.value) || 0) }))}
                    className="ml-auto w-24 text-right text-xs"
                    title={real ? `Uso real dos últimos 30 dias: ${tokens(f.last30Days.calls)} (${usd(f.last30Days.costUsd)})` : 'Premissa (docs/ia-custos.md): ainda sem uso nos últimos 30 dias'}
                  />
                  <div className="mt-0.5 text-[10px] text-fg-soft">{volume[f.id] !== undefined ? 'alterado' : real ? 'real, 30 dias' : 'premissa'}</div>
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">{monthly === null ? '—' : usd(monthly)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border bg-muted text-xs font-medium">
            <tr>
              <td className="px-3 py-2" colSpan={4}>
                Total dos recursos ligados
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{usd(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-fg-soft">
        Cada recurso usa o modelo escolhido para ele (ou o padrão) e o preço desse modelo. Por uso: tamanho típico do pedido de cada recurso (docs/ia-custos.md),
        com o raciocínio no nível baixo. Usos por mês: o real dos últimos 30 dias quando houve uso; senão a premissa para ~100 atendimentos por dia. Mude o número
        para simular. O valor gasto de fato está em "Consumo", abaixo.
      </p>
    </section>
  );
}

/** Consumo de tokens e custo estimado por recurso no período (registrado a cada chamada). */
function AiUsage() {
  const [from, setFrom] = useState(() => localDay(new Date()).slice(0, 8) + '01');
  const [to, setTo] = useState(() => localDay(new Date()));
  const { data, error, isFetching } = useQuery({
    queryKey: ['ai-usage', from, to],
    queryFn: () => get<UsageView>(`/ai/usage?from=${from}&to=${to}`),
    enabled: Boolean(from && to),
  });
  const days = data?.byDay.length ?? 0;
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Consumo {isFetching && <Spinner />}</h2>
        <div className="flex items-center gap-1.5 text-xs">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36 text-xs" />
          até
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36 text-xs" />
        </div>
      </div>
      <ErrorText error={error} />
      {data && (
        <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Recurso</th>
              <th className="px-3 py-2">Modelo</th>
              <th className="px-3 py-2 text-right">Usos</th>
              <th className="px-3 py-2 text-right">Tokens de entrada</th>
              <th className="px-3 py-2 text-right" title="Inclui o raciocínio do modelo">
                Tokens de saída
              </th>
              <th className="px-3 py-2 text-right">Custo estimado</th>
            </tr>
          </thead>
          <tbody>
            {data.byFeature.map((r) => (
              <tr key={`${r.feature}|${r.model}`} className="border-t border-border">
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-xs">{r.modelLabel}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(r.calls)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(r.inputTokens)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(r.outputTokens)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{usd(r.costUsd)}</td>
              </tr>
            ))}
            {!data.byFeature.length && (
              <tr>
                <td colSpan={6} className="px-3 py-3 text-center text-xs text-fg-soft">
                  Nenhum uso da IA no período.
                </td>
              </tr>
            )}
          </tbody>
          {data.byFeature.length > 0 && (
            <tfoot className="border-t border-border bg-muted text-xs font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={2}>
                  Total{days > 1 ? ` (média de ${usd(data.total.costUsd / days)} por dia com uso)` : ''}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(data.total.calls)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(data.total.inputTokens)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tokens(data.total.outputTokens)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{usd(data.total.costUsd)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
      <p className="mt-1 text-[11px] text-fg-soft">
        Tokens informados pelo provedor a cada chamada; custo estimado pelo preço do modelo que respondeu (um recurso que trocou de modelo no período aparece
        em uma linha por modelo). O valor cobrado de fato está no painel do provedor.
      </p>
    </section>
  );
}
